import React from 'react'
import classnames from 'classnames/bind'
import { useSpring, animated, easings } from '@react-spring/web'
import { getCssColorFromColorName } from '../../../utils/colors'
import { ColorNames } from '../../../constants/colorVars'
import { AutomationTarget } from '../../../types'

import styles from './styles.scss'

const cx = classnames.bind(styles)

const SCALE_MIN = 0.05
const SCALE_MAX = 1
const OPACITY_MIN = 0
const OPACITY_MAX = 0.5
const DURATION_FADE_IN_MS = 1000
const DURATION_FADE_OUT_MS = 800

/**
 * Pulser renders a pulsing circle with a specified color and width.
 * NOTE: We could parameterize the timing if we decide it's needed later,
 * but for now assume it's a YAGNI situation.
 */
interface PulserProps extends AutomationTarget {
  width?: number
  color?: ColorNames
  className: string
}

const Pulser: React.FC<PulserProps> = ({
  width = 100,
  color = ColorNames.Purple,
  elmId = 'pulser',
  className,
}) => {
  const colorRaw = getCssColorFromColorName(color)

  const styles = useSpring({
    from: { scale: SCALE_MIN, opacity: OPACITY_MIN },
    to: async next => {
      while (true) {
        // coming in to 70% size, intensifying
        await next({
          scale: SCALE_MAX * 0.7,
          opacity: OPACITY_MAX,
          config: {
            duration: DURATION_FADE_IN_MS,
            easing: easings.easeOutCubic,
          },
        })
        // going out to 100% size, fading
        await next({
          scale: SCALE_MAX,
          opacity: OPACITY_MIN,
          config: {
            duration: DURATION_FADE_OUT_MS,
            easing: easings.easeOutQuad,
          },
        })
        // reset to beginning
        await next({ scale: SCALE_MIN, opacity: OPACITY_MIN, immediate: true })
      }
    },
  })

  const sizeStyle = width
    ? { width, height: width }
    : { width: '100%', height: '100%' }

  return (
    <animated.div
      data-elm-id={elmId}
      style={{
        ...styles,
        backgroundColor: colorRaw,
        ...sizeStyle,
      }}
      className={cx('pulser', className)}
    />
  )
}

export default Pulser
